import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Flex, Table } from '@televet/kibble-ui/chakra';
import { Button } from '@televet/kibble-ui/Button';
import { Spinner } from '@televet/kibble-ui/Spinner';
import { Tag } from '@televet/kibble-ui/Tag';
import { Text } from '@televet/kibble-ui/Text';
import { trpc } from '@/lib/trpc';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { PageContainer } from '@/components/PageContainer';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { toaster } from '@/components/ui/toaster';
import { RowActionButton } from '@/components/RowActionButton';
import { EnrollClinicDialog } from '@/routes/_authenticated/emitters/-components/EnrollClinicDialog';
import { InstanceFilters } from '@/routes/_authenticated/emitters/-components/InstanceFilters';
import { InstanceStatusTag } from '@/routes/_authenticated/emitters/-components/StatusTag';
import {
  instanceFiltersSchema,
  type InstanceFilterValues,
} from '@/routes/_authenticated/emitters/-lib/instanceFilters';
import { useListingFilters } from '@/features/listing-filters/useListingFilters';
import { useDebouncedFilterText } from '@/features/listing-filters/useDebouncedFilterText';

export const Route = createFileRoute('/_authenticated/emitter-templates/$templateId/instances/')({
  validateSearch: instanceFiltersSchema,
  component: TemplateInstances,
});

const STATUS_OPTIONS = ['All', 'Active', 'Paused', 'Completed', 'Archived'] as const;

function TemplateInstances() {
  const { templateId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const routeNavigate = Route.useNavigate();
  const filters = Route.useSearch();
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{
    type: 'pause' | 'resume' | 'archive' | 'trigger';
    instanceId: string;
    instanceName: string;
  } | null>(null);

  const { data: template } = useQuery(trpc.emitter.getTemplate.queryOptions({ id: templateId }));

  const {
    data: instancesData,
    isLoading,
    error,
  } = useQuery(
    trpc.emitter.listInstances.queryOptions(
      useMemo(
        () => ({
          // Always scoped to this template, so the Template filter is left out
          // of the dropdown below.
          templateId: [templateId],
          ...(filters.status?.length && { status: filters.status }),
          ...(filters.ownerType?.length && { ownerType: filters.ownerType }),
          ...(filters.scheduleType?.length && { scheduleType: filters.scheduleType }),
          ...(filters.search && { search: filters.search }),
        }),
        [templateId, filters]
      ),
      { placeholderData: keepPreviousData }
    )
  );
  const instances = instancesData?.items;

  const [searchDraft, setSearchDraft] = useDebouncedFilterText({
    value: filters.search,
    onCommit: (next) =>
      routeNavigate({ replace: true, search: (prev) => ({ ...prev, search: next }) }),
  });

  const applyFilters = useCallback(
    (next: InstanceFilterValues) => {
      routeNavigate({ search: next });
    },
    [routeNavigate]
  );

  // Keyed separately from the all-instances list: the filters that make sense
  // inside one template are not the ones you last used across all of them.
  useListingFilters({
    page: 'emitter-template-instances',
    schema: instanceFiltersSchema,
    filters,
    onRehydrate: applyFilters,
  });

  const invalidate = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: [['emitter', 'listInstances']],
      }),
    [queryClient]
  );

  const { mutate: pauseInstance, isPending: isPausing } = useMutation(
    trpc.emitter.pauseInstance.mutationOptions({
      onSuccess: () => {
        toaster.success({ title: 'Instance paused' });
        setConfirmAction(null);
        invalidate();
      },
      onError: (err) => {
        toaster.error({ title: 'Failed to pause', description: err.message });
        setConfirmAction(null);
      },
    })
  );

  const { mutate: resumeInstance, isPending: isResuming } = useMutation(
    trpc.emitter.resumeInstance.mutationOptions({
      onSuccess: () => {
        toaster.success({ title: 'Instance resumed' });
        setConfirmAction(null);
        invalidate();
      },
      onError: (err) => {
        toaster.error({ title: 'Failed to resume', description: err.message });
        setConfirmAction(null);
      },
    })
  );

  const { mutate: archiveInstance, isPending: isArchiving } = useMutation(
    trpc.emitter.archiveInstance.mutationOptions({
      onSuccess: () => {
        toaster.success({ title: 'Instance archived' });
        setConfirmAction(null);
        invalidate();
      },
      onError: (err) => {
        toaster.error({
          title: 'Failed to archive',
          description: err.message,
        });
        setConfirmAction(null);
      },
    })
  );

  const { mutate: createInstance, isPending: isCreating } = useMutation(
    trpc.emitter.createInstance.mutationOptions({
      onSuccess: () => {
        toaster.success({ title: 'Enrolled successfully' });
        setEnrollOpen(false);
        invalidate();
      },
      onError: (err) => {
        toaster.error({
          title: 'Failed to enroll',
          description: err.message,
        });
      },
    })
  );

  const { mutate: triggerExecution, isPending: isTriggering } = useMutation(
    trpc.emitter.triggerExecution.mutationOptions({
      onSuccess: (_data, variables) => {
        toaster.success({ title: 'Execution triggered' });
        setConfirmAction(null);
        invalidate();
        navigate({
          to: `/emitter-templates/${templateId}/instances/${variables.instanceId}/executions` as string,
        });
      },
      onError: (err) => {
        toaster.error({
          title: 'Failed to trigger',
          description: err.message,
        });
        setConfirmAction(null);
      },
    })
  );

  const handleConfirm = () => {
    if (!confirmAction) return;
    switch (confirmAction.type) {
      case 'pause':
        pauseInstance({ id: confirmAction.instanceId });
        break;
      case 'resume':
        resumeInstance({ id: confirmAction.instanceId });
        break;
      case 'archive':
        archiveInstance({ id: confirmAction.instanceId });
        break;
      case 'trigger':
        triggerExecution({ instanceId: confirmAction.instanceId });
        break;
    }
  };

  const confirmLabels: Record<
    string,
    { title: string; description: string; label: string; color?: string }
  > = {
    pause: {
      title: 'Pause Instance',
      description: `Pause "${confirmAction?.instanceName}"? Scheduled executions will be skipped while paused.`,
      label: 'Pause',
      color: 'yellow',
    },
    resume: {
      title: 'Resume Instance',
      description: `Resume "${confirmAction?.instanceName}"? Scheduled executions will begin running again.`,
      label: 'Resume',
      color: 'green',
    },
    archive: {
      title: 'Archive Instance',
      description: `Archive "${confirmAction?.instanceName}"? This instance will no longer execute and cannot be resumed.`,
      label: 'Archive',
      color: 'red',
    },
    trigger: {
      title: 'Trigger Execution',
      description: `Trigger a Full execution for "${confirmAction?.instanceName}"? This will persist results and emit events.`,
      label: 'Trigger',
      color: 'blue',
    },
  };

  const templateName = template?.name ?? 'Template';

  return (
    <PageContainer>
      <Flex direction="column" gap={3} width="100%">
        <Breadcrumbs
          items={[
            { label: 'Templates', to: '/emitter-templates' },
            { label: templateName, to: `/emitter-templates/${templateId}` },
            { label: 'Instances' },
          ]}
        />

        <Flex justify="space-between" align="center">
          <Text fontSize="lg" fontWeight="semibold" letterSpacing="-0.01em">
            Instances -- {templateName}
          </Text>
          <Button size="sm" iconName="plus" onClick={() => setEnrollOpen(true)}>
            Enroll
          </Button>
        </Flex>

        <InstanceFilters
          values={filters}
          onChange={applyFilters}
          templates={[]}
          searchDraft={searchDraft}
          onSearchDraftChange={setSearchDraft}
        />

        {isLoading && (
          <Flex justify="center" py={8}>
            <Spinner size="lg" centered={false} />
          </Flex>
        )}

        {error && (
          <Flex justify="center" py={6}>
            <Text fontSize="sm" variant="danger">
              Failed to load instances: {error.message}
            </Text>
          </Flex>
        )}

        {!isLoading && !error && (
          <Table.Root size="sm" width="100%" variant="outline">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>Name</Table.ColumnHeader>
                <Table.ColumnHeader>Owner</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="center">Version</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="center">Schedule</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="center">Status</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="center">Runs</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="end" />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {instances?.map((instance) => (
                <Table.Row
                  key={instance.id}
                  cursor="pointer"
                  _hover={{ bg: 'bg.muted' }}
                  onClick={() =>
                    navigate({
                      to: '/emitters/$instanceId',
                      params: { instanceId: instance.id },
                    })
                  }
                >
                  <Table.Cell>
                    <Text fontSize="sm" fontWeight="medium">
                      {instance.name ?? '-'}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Text fontSize="xs" variant="subtle" fontFamily="mono" truncated maxW="220px">
                      {instance.ownerType}:{instance.ownerId}
                      {instance.ownerName ? ` (${instance.ownerName})` : ''}
                    </Text>
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    <Text fontSize="xs" variant="subtle">
                      v{instance.templateVersion.versionNumber}
                    </Text>
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    <Tag label={instance.configuration?.scheduleType ?? 'N/A'} size="sm" />
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    <InstanceStatusTag status={instance.status} />
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    <Text fontSize="xs" variant="subtle">
                      {instance._count.executions}
                    </Text>
                  </Table.Cell>
                  <Table.Cell textAlign="end">
                    <Flex gap={0.5} justify="flex-end" onClick={(e) => e.stopPropagation()}>
                      {instance.status === 'Active' && (
                        <RowActionButton
                          label="Pause"
                          iconName="pause"
                          onClick={() =>
                            setConfirmAction({
                              type: 'pause',
                              instanceId: instance.id,
                              instanceName: instance.name ?? instance.template.name,
                            })
                          }
                          data-testid={`template-instance-pause-${instance.id}`}
                        />
                      )}
                      {instance.status === 'Paused' && (
                        <RowActionButton
                          label="Resume"
                          iconName="play"
                          onClick={() =>
                            setConfirmAction({
                              type: 'resume',
                              instanceId: instance.id,
                              instanceName: instance.name ?? instance.template.name,
                            })
                          }
                          data-testid={`template-instance-resume-${instance.id}`}
                        />
                      )}
                      {instance.status !== 'Archived' && (
                        <RowActionButton
                          label="Archive"
                          iconName="archive"
                          variant="ghostDestructive"
                          onClick={() =>
                            setConfirmAction({
                              type: 'archive',
                              instanceId: instance.id,
                              instanceName: instance.name ?? instance.template.name,
                            })
                          }
                          data-testid={`template-instance-archive-${instance.id}`}
                        />
                      )}
                      <RowActionButton
                        label="Edit"
                        iconName="pen"
                        onClick={() =>
                          navigate({
                            to: '/emitters/$instanceId',
                            params: { instanceId: instance.id },
                          })
                        }
                        data-testid={`template-instance-edit-${instance.id}`}
                      />
                      <RowActionButton
                        label="Executions"
                        iconName="clock"
                        onClick={() =>
                          navigate({
                            to: `/emitter-templates/${templateId}/instances/${instance.id}/executions` as string,
                          })
                        }
                        data-testid={`template-instance-executions-${instance.id}`}
                      />
                      {/* triggerExecution rejects any instance that is not Active. */}
                      {instance.status === 'Active' && (
                        <RowActionButton
                          label="Run now"
                          iconName="magic"
                          variant="ghost"
                          onClick={() =>
                            setConfirmAction({
                              type: 'trigger',
                              instanceId: instance.id,
                              instanceName: instance.name ?? instance.template.name,
                            })
                          }
                          data-testid={`template-instance-run-now-${instance.id}`}
                        />
                      )}
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        )}

        {!isLoading && !error && instances?.length === 0 && (
          <Flex justify="center" py={6}>
            <Text fontSize="sm" variant="subtle">
              No instances for this template
            </Text>
          </Flex>
        )}
      </Flex>

      <EnrollClinicDialog
        isOpen={enrollOpen}
        onClose={() => setEnrollOpen(false)}
        onSubmit={createInstance}
        isSubmitting={isCreating}
        defaultTemplateId={templateId}
      />

      {confirmAction && (
        <ConfirmDialog
          isOpen
          title={confirmLabels[confirmAction.type].title}
          description={confirmLabels[confirmAction.type].description}
          confirmLabel={confirmLabels[confirmAction.type].label}
          variant={
            confirmLabels[confirmAction.type].color === 'red' ? 'primaryDestructive' : 'primary'
          }
          onConfirm={handleConfirm}
          onCancel={() => setConfirmAction(null)}
          isLoading={isPausing || isResuming || isArchiving || isTriggering}
        />
      )}
    </PageContainer>
  );
}
